import React, { useEffect, useState } from 'react';
import { 
  XMarkIcon,
  SunIcon,
  MoonIcon,
  StarIcon,
  SparklesIcon,
  HeartIcon,
  BoltIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { AstrologyData } from '../services/astrologyApi';
import { userService } from '../services/api';
import AuroraLoadingOverlay from './AuroraLoadingOverlay';

interface FullAstralMapProps {
  isOpen: boolean;
  onClose: () => void;
  astralData: AstrologyData | null;
  birthChart?: any;
}

const FullAstralMap: React.FC<FullAstralMapProps> = ({ isOpen, onClose, astralData, birthChart }) => {
  const [chart, setChart] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    let active = true;
    async function ensureChart() {
      if (!isOpen) return;
      if (chart) return;
      // Não usar `birthChart` (prop vindo do Dashboard, cache local do perfil)
      // como atalho aqui: ele pode estar desatualizado em relação ao que o
      // backend calcula/gera via IA (gate de cache fica em /mapa-completo,
      // não no que o Dashboard já carregou). Sempre busca a versão
      // enriquecida — se o cache do backend já estiver válido, a resposta
      // volta rápido sem gastar IA de novo; só regenera quando necessário.
      try {
        setLoading(true);
        try {
          const enriched = await userService.getFullAstralMap();
          if (active) { setChart(enriched); setLoading(false); return; }
        } catch {}
        const bc = await userService.getBirthChart();
        if (active) setChart(bc);
      } catch {
        try {
          const bcg = await userService.generateBirthChart();
          if (active) setChart(bcg);
        } catch {}
      }
      if (active) setLoading(false);
    }
    ensureChart();
    return () => { active = false; };
  }, [isOpen, birthChart]);
  const chartData = chart || birthChart;
  if (!isOpen) return null;
  if (loading || !astralData) return <AuroraLoadingOverlay isLoading={true} message="Montando seu Mapa Astral..." />;
  const report = (chartData as any)?.detailed_report || {};

  type HouseUI = { number: number; name: string; description: string };
  const defaultHouses: HouseUI[] = [
    { number: 1, name: 'Casa da Personalidade', description: 'Sua identidade e como você se apresenta ao mundo' },
    { number: 2, name: 'Casa dos Recursos', description: 'Valores pessoais, talentos e recursos materiais' },
    { number: 3, name: 'Casa da Comunicação', description: 'Comunicação, aprendizado e relacionamentos próximos' },
    { number: 4, name: 'Casa do Lar', description: 'Família, raízes e base emocional' },
    { number: 5, name: 'Casa da Criatividade', description: 'Expressão criativa, romance e diversão' },
    { number: 6, name: 'Casa do Trabalho', description: 'Rotina diária, saúde e serviço' },
    { number: 7, name: 'Casa dos Relacionamentos', description: 'Parcerias, casamento e inimigos declarados' },
    { number: 8, name: 'Casa da Transformação', description: 'Transformação, recursos compartilhados e mistérios' },
    { number: 9, name: 'Casa da Filosofia', description: 'Filosofia, viagens e educação superior' },
    { number: 10, name: 'Casa da Carreira', description: 'Carreira, reputação e objetivos de vida' },
    { number: 11, name: 'Casa dos Amigos', description: 'Amizades, grupos e esperanças' },
    { number: 12, name: 'Casa do Inconsciente', description: 'Espiritualidade, karma e inconsciente' }
  ];

  const houses: HouseUI[] = Array.isArray(chartData?.houses)
    ? chartData.houses.map((h: any) => ({
        number: h.house,
        name: `Casa ${h.house}`,
        description: h.interpretation || `Cúpula: ${h.cusp_sign}`,
        cuspSign: h.cusp_sign,
        cuspDegree: h.cusp_degree,
      }))
    : defaultHouses;

  const planetInfo: Record<string, { symbol: string; meaning: string }> = {
    Sol: { symbol: '☉', meaning: 'Identidade, ego, vitalidade' },
    Lua: { symbol: '☽', meaning: 'Emoções, instintos, inconsciente' },
    Ascendente: { symbol: 'Asc', meaning: 'Como você se apresenta e inicia' },
    Mercúrio: { symbol: '☿', meaning: 'Comunicação, pensamento, aprendizado' },
    Vênus: { symbol: '♀', meaning: 'Amor, beleza, valores' },
    Marte: { symbol: '♂', meaning: 'Ação, energia, desejo' },
    Júpiter: { symbol: '♃', meaning: 'Expansão, sabedoria, sorte' },
    Saturno: { symbol: '♄', meaning: 'Disciplina, responsabilidade, limites' },
    Urano: { symbol: '♅', meaning: 'Mudanças, inovação, libertação' },
    Netuno: { symbol: '♆', meaning: 'Espiritualidade, imaginação, sensibilidade' },
    Plutão: { symbol: '♇', meaning: 'Transformações profundas, poder' },
  };

  type PlanetUI = { name: string; symbol: string; meaning: string; position: string; interpretation?: string; sign?: string; house?: number; degree?: number; retrograde?: boolean };
  const planets: PlanetUI[] = Array.isArray(chartData?.planets)
    ? chartData.planets.map((p: any) => ({
        name: p.planet,
        symbol: planetInfo[p.planet]?.symbol || '',
        meaning: planetInfo[p.planet]?.meaning || '',
        position: `${p.sign} • Casa ${p.house} • ${Number(p.degree).toFixed(1)}°`,
        interpretation: p.interpretation,
        sign: p.sign,
        house: p.house,
        degree: p.degree,
        retrograde: p.retrograde,
      }))
    : [];

  const solarSign = Array.isArray(chartData?.planets)
    ? (chartData.planets.find((p: any) => p.planet === 'Sol')?.sign || astralData.sign)
    : astralData.sign;
  const ascSign = Array.isArray(chartData?.planets)
    ? (chartData.planets.find((p: any) => p.planet === 'Ascendente')?.sign || null)
    : null;
  const mcSign = (() => {
    if ((chartData as any)?.angles?.mc?.sign) return String((chartData as any).angles.mc.sign);
    const h10 = Array.isArray((chartData as any)?.houses) ? (chartData as any).houses.find((h: any) => Number(h.house) === 10) : null;
    return h10?.cusp_sign || null;
  })();
  const ascDeg = (() => {
    const d = (chartData as any)?.angles?.asc?.degree;
    return typeof d === 'number' ? d : null;
  })();
  const mcDeg = (() => {
    const d = (chartData as any)?.angles?.mc?.degree;
    return typeof d === 'number' ? d : null;
  })();
  const fmtDeg = (deg?: number | null) => {
    if (typeof deg !== 'number') return null;
    const d = Math.floor(deg);
    const m = Math.floor((deg - d) * 60);
    const s = Math.round((((deg - d) * 60) - m) * 60);
    return `${String(d).padStart(2,'0')}°${String(m).padStart(2,'0')}'${String(s).padStart(2,'0')}"`;
  };

  const anglesReport = (report as any).angles || {};
  const planetReport: { title: string; interpretation: string }[] = (report as any).planets || [];
  const housesReport: { title: string; interpretation: string }[] = (report as any).houses || [];
  const aspectsReport: string[] = (report as any).aspects || [];
  const polarization = (report as any).polarization || {};
  const mission = (report as any).mission as any;
  const fortune = (report as any).fortune as string | undefined;
  const planetsByHouse: Record<string, string[]> = (report as any).planets_by_house || {};
  const potentials = (report as any).potentials || (chartData as any)?.potentials || { harmonic: [], disharmonic: [] };
  const nodesReport: { title: string; interpretation: string }[] = Array.isArray((report as any).nodes) ? (report as any).nodes : [];
  const missionSummary: string = (() => {
    const m = mission as any;
    if (m && typeof m === 'object') {
      return String(m.frase_chave || m.frase_base || '');
    }
    const base = (chartData as any)?.mission;
    const text = typeof base === 'string' ? base : (base && typeof base === 'object' ? (base.interpretacao || base.frase || '') : '');
    return text;
  })();


  const signImageMap: Record<string, string> = {
    'aries': 'aries', 'áries': 'aries',
    'touro': 'touro',
    'gêmeos': 'gemeos', 'gemeos': 'gemeos',
    'câncer': 'cancer', 'cancer': 'cancer',
    'leão': 'leao', 'leao': 'leao',
    'virgem': 'virgem',
    'libra': 'libra',
    'escorpião': 'escorpiao', 'escorpiao': 'escorpiao',
    'sagitário': 'sargitario', 'sagitario': 'sargitario',
    'capricórnio': 'capricornio', 'capricornio': 'capricornio',
    'aquário': 'aquario', 'aquario': 'aquario',
    'peixes': 'peixes',
  };
  const moonImageMap: Record<string, string> = {
    'lua nova': 'luanova',
    'nova': 'luanova',
    'crescente côncava': 'crescenteconcava',
    'crescente concava': 'crescenteconcava',
    'lua crescente': 'crescenteconcava',
    'crescente': 'crescenteconcava',
    'quarto crescente': 'quartocrescente',
    'primeiro quarto': 'quartocrescente',
    'crescente gibosa': 'crescentegibosa',
    'gibosa crescente': 'crescentegibosa',
    'lua cheia': 'luacheia',
    'cheia': 'luacheia',
    'minguante gibosa': 'minguantegibosa',
    'gibosa minguante': 'minguantegibosa',
    'quarto minguante': 'quartominguante',
    'último quarto': 'quartominguante',
    'ultimo quarto': 'quartominguante',
    'minguante côncava': 'minguanteconcava',
    'minguante concava': 'minguanteconcava',
    'lua minguante': 'minguanteconcava',
    'minguante': 'minguanteconcava',
  };
  const getSignImage = (sign?: string | null) => {
    if (!sign) return null;
    const key = sign.toLowerCase().trim();
    const file = signImageMap[key];
    return file ? `/signos/${file}.png` : null;
  };
  const getMoonImage = (phase?: string | null) => {
    if (!phase) return null;
    const key = phase.toLowerCase().trim();
    const file = moonImageMap[key];
    return file ? `/lua/${file}.png` : null;
  };

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center gap-3 mb-6">
      <span className="text-amber-400 text-sm">✦</span>
      <h3 className="text-xs font-bold text-slate-200 uppercase tracking-[0.2em]">{children}</h3>
      <div className="flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1c1c38] rounded-3xl max-w-5xl w-full max-h-[92vh] overflow-y-auto hide-scrollbar border border-white/[0.14] shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 bg-[#1c1c38]/95 backdrop-blur-md z-10 px-8 py-5 border-b border-white/[0.12]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-amber-400 text-xl">✦</span>
              <div>
                <h2 className="text-base font-bold text-white tracking-[0.15em] uppercase">Mapa Astral Completo</h2>
                <p className="text-xs text-slate-400 tracking-wide">Carta natal detalhada</p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full border border-white/10 hover:bg-white/10 flex items-center justify-center transition-colors group">
              <XMarkIcon className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
            </button>
          </div>
        </div>

        <div className="px-8 py-8 space-y-10">

          {/* Loading */}
          {loading && !chartData && (
            <div className="py-16 flex flex-col items-center gap-4 text-center">
              <div className="w-10 h-10 rounded-full border-2 border-purple-500/40 border-t-purple-400 animate-spin" />
              <p className="text-slate-200 font-medium">Aurora Lyz está interpretando seu Mapa Astral…</p>
              <p className="text-slate-400 text-sm max-w-xs">Isso leva alguns segundos. Salvaremos no seu perfil para abrir instantaneamente da próxima vez.</p>
            </div>
          )}

          {/* Hero — signos principais */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Solar', symbol: '☉', value: solarSign, sub: astralData.element, color: 'text-amber-300', type: 'sign' },
              ascSign ? { label: 'Ascendente', symbol: '↑', value: ascSign, sub: fmtDeg(ascDeg) || '—', color: 'text-purple-300', type: 'sign' } : null,
              { label: 'Fase Lunar', symbol: '☽', value: astralData.moonPhase, sub: '', color: 'text-blue-300', type: 'moon' },
              mcSign ? { label: 'Meio do Céu', symbol: 'MC', value: mcSign, sub: fmtDeg(mcDeg) || '—', color: 'text-teal-300', type: 'mc' } : null,
            ].filter(Boolean).map((item: any, i) => {
              const img = item.type === 'sign' ? getSignImage(item.value) : item.type === 'moon' ? getMoonImage(item.value) : item.type === 'mc' ? '/meiodoceu.png' : null;
              return (
                <div key={i} className="bg-white/[0.08] border border-white/[0.14] rounded-2xl p-5 text-center hover:bg-white/[0.13] transition-colors">
                  {img ? (
                    <img src={img} alt={item.value} className="w-28 h-28 mx-auto mb-3 object-contain opacity-90" />
                  ) : (
                    <div className="text-4xl mb-3 opacity-80">{item.symbol}</div>
                  )}
                  <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">{item.label}</div>
                  <div className={`text-xl font-bold ${item.color}`}>{item.value}</div>
                  {item.sub && <div className="text-sm text-slate-400 mt-1">{item.sub}</div>}
                </div>
              );
            })}
          </div>

          {/* Ascendente + Meio do Céu — o que cada ângulo significa */}
          {(ascSign || mcSign) && (
            <div>
              <SectionTitle>Ascendente & Meio do Céu</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ascSign ? {
                    label: 'Ascendente', sign: ascSign, deg: fmtDeg(ascDeg),
                    note: anglesReport.ascendant?.text || anglesReport.ascendant?.note || 'Como você se apresenta e inicia as coisas: a primeira impressão que causa e o jeito instintivo de agir diante do novo.',
                  } : null,
                  mcSign ? {
                    label: 'Meio do Céu (MC)', sign: mcSign, deg: fmtDeg(mcDeg),
                    note: anglesReport.mc?.text || anglesReport.mc?.note || 'Carreira e direção pública: a imagem que você projeta profissionalmente e a vocação que busca reconhecimento.',
                  } : null,
                ].filter(Boolean).map((item: any) => (
                  <div key={item.label} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-5">
                    <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">{item.label}</div>
                    <div className="text-lg font-semibold text-white mb-2">{item.sign} {item.deg ? <span className="text-sm text-slate-400 font-normal">• {item.deg}</span> : null}</div>
                    <p className="text-xs text-slate-300 leading-relaxed text-justify">{item.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Interpretação personalizada */}
          {((report as any).sign_text || astralData.sign) && (
            <div>
              <SectionTitle>Interpretação Personalizada</SectionTitle>
              <p className="text-slate-300 text-sm leading-relaxed text-justify">
                {(report as any).sign_text || `Como ${astralData.sign}, você manifesta o elemento ${astralData.element.toLowerCase()} com o regente ${astralData.planet}.`}
              </p>
            </div>
          )}

          {/* Missão de Vida */}
          {(() => {
            const m = mission as any;
            const hasStructured = m && typeof m === 'object' && (Array.isArray(m.ponto_partida) || Array.isArray(m.destino_evolutivo));
            const base = (chartData as any)?.mission;
            const baseText = typeof base === 'string' ? base : (base && typeof base === 'object' ? (base.interpretacao || base.frase || '') : '');
            if (!hasStructured && !baseText) return null;
            return (
              <div>
                <SectionTitle>Missão de Vida</SectionTitle>
                <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/20 rounded-2xl p-6 space-y-4 text-sm text-slate-300">
                  {hasStructured ? (
                    <>
                      {[
                        { label: 'Ponto de Partida', items: m.ponto_partida },
                        { label: 'Destino Evolutivo', items: m.destino_evolutivo },
                        { label: 'Como se Manifesta', items: m.manifestacao_pratica },
                        { label: 'Potenciais Integrados', items: m.potenciais_integrados },
                      ].map(({ label, items }) => Array.isArray(items) && items.length > 0 && (
                        <div key={label}>
                          <div className="text-xs text-purple-300 uppercase tracking-widest mb-2">{label}</div>
                          <ul className="space-y-1">
                            {items.map((t: string, i: number) => <li key={i} className="flex gap-2"><span className="text-purple-400 mt-0.5">·</span>{t}</li>)}
                          </ul>
                        </div>
                      ))}
                      {m.conflito && <div><div className="text-xs text-purple-300 uppercase tracking-widest mb-1">Conflito Central</div><p className="text-justify">{String(m.conflito)}</p></div>}
                      {m.frase_chave && <div className="pt-2 border-t border-white/10 text-purple-200 italic">"{String(m.frase_chave)}"</div>}
                    </>
                  ) : <p className="text-justify">{baseText}</p>}
                </div>
              </div>
            );
          })()}

          {/* Planetas */}
          <div>
            <SectionTitle>Posições Planetárias</SectionTitle>
            {!planets.length ? (
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <div className="w-4 h-4 rounded-full border-2 border-purple-400/50 border-t-purple-400 animate-spin" />
                <span>Calculando posições…</span>
              </div>
            ) : (
              <div className="space-y-6">
                {[
                  { key: 'luminares', label: 'Os Luminares', names: ['Sol', 'Lua'], desc: 'Os dois astros centrais da identidade: o Sol mostra quem você é por essência, a Lua mostra como você sente e reage por instinto.' },
                  { key: 'pessoais', label: 'Planetas Pessoais', names: ['Mercúrio', 'Vênus', 'Marte'], desc: 'Regem o dia a dia — como você pensa e se comunica, como ama e valoriza, como age e desperta desejo. Mudam de signo rápido, por isso são bem individuais.' },
                  { key: 'sociais', label: 'Planetas Sociais', names: ['Júpiter', 'Saturno'], desc: 'A ponte entre você e o mundo: onde busca expansão, oportunidade e sorte (Júpiter), e onde encontra limite, disciplina e responsabilidade (Saturno).' },
                  { key: 'transpessoais', label: 'Planetas Transpessoais (Geracionais)', names: ['Urano', 'Netuno', 'Plutão'], desc: 'Movem-se devagar e marcam gerações inteiras, não só você — falam de rupturas, inspiração e transformações profundas que atravessam sua vida.' },
                ].map((cat) => {
                  const items = planets.filter((p) => cat.names.includes(String(p.name)));
                  if (!items.length) return null;
                  return (
                    <div key={cat.key}>
                      <div className="mb-3">
                        <h4 className="text-xs font-bold text-amber-300 uppercase tracking-widest">{cat.label}</h4>
                        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{cat.desc}</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map((planet, i) => (
                          <div key={i} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4 hover:bg-white/[0.13] transition-colors">
                            <div className="flex items-start gap-3 mb-2">
                              <span className="text-2xl leading-none mt-0.5 opacity-90">{planet.symbol}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-white text-sm">{planet.name}</div>
                                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                  <span className="text-xs text-amber-300/80">{planet.sign}</span>
                                  {typeof planet.house !== 'undefined' && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-white/[0.07] border border-white/10 rounded-full text-slate-400">Casa {planet.house}</span>
                                  )}
                                  {typeof planet.degree !== 'undefined' && (
                                    <span className="text-[10px] text-slate-500">{Number(planet.degree).toFixed(1)}°{planet.retrograde ? ' ℞' : ''}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed text-justify">{planet.interpretation || planet.meaning}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Casas */}
          <div>
            <SectionTitle>Casas Astrológicas</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(housesReport.length > 0 ? housesReport : houses).map((h: any, i) => (
                <div key={i} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4 hover:bg-white/[0.13] transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-7 h-7 shrink-0 rounded-full bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
                      <span className="text-amber-400 text-xs font-bold">{housesReport.length > 0 ? i + 1 : (h as any).number}</span>
                    </div>
                    <h4 className="text-sm font-medium text-white truncate">{housesReport.length > 0 ? h.title : (h as any).name}</h4>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed text-justify">{housesReport.length > 0 ? h.interpretation : (h as any).description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Aspectos */}
          {Array.isArray((chartData as any)?.aspects) && (chartData as any)?.aspects.length > 0 && (
            <div>
              <SectionTitle>Aspectos Planetários</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(chartData as any).aspects.map((a: any, i: number) => (
                  <div key={i} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4">
                    <div className="text-sm font-medium text-white mb-1">{a.planet1} <span className="text-purple-400">{a.type}</span> {a.planet2}</div>
                    <div className="text-[10px] text-slate-400 mb-2">Orbe {a.orb}°</div>
                    <p className="text-xs text-slate-400 leading-relaxed text-justify">{a.interpretation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nodo Norte + Parte da Fortuna + Lilith */}
          <div>
            <SectionTitle>Karma e Fortuna</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                {
                  label: 'Nodo Norte',
                  text: (() => {
                    const ft = Array.isArray((report as any)?.five_texts) ? (report as any).five_texts[0] : null;
                    if (ft) return ft;
                    const nn = nodesReport.find((n: any) => String(n.title).toLowerCase().includes('nodo norte'));
                    return nn?.interpretation || 'Caminho de evolução pessoal com foco em escolhas conscientes.';
                  })(),
                  icon: '☊',
                },
                {
                  label: 'Parte da Fortuna',
                  text: (() => {
                    const ft = Array.isArray((report as any)?.five_texts) ? (report as any).five_texts[1] : null;
                    if (ft) return ft;
                    return fortune || 'Espaço de fluidez e sorte natural, ligado ao que traz alegria e propósito.';
                  })(),
                  icon: '⊕',
                },
                (chartData as any)?.lilith ? {
                  label: 'Lilith (Lua Negra)',
                  sub: `${(chartData as any).lilith.sign} • Casa ${(chartData as any).lilith.house}`,
                  text: (chartData as any).lilith.interpretation || 'O instinto e a sombra reprimida — o que você esconde ou reprime até aceitar como parte do seu poder pessoal.',
                  icon: '⚸',
                } : null,
              ].filter(Boolean).map(({ label, text, icon, sub }: any) => (
                <div key={label} className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-teal-400 text-lg">{icon}</span>
                    <div className="text-xs text-slate-400 uppercase tracking-widest">{label}</div>
                  </div>
                  {sub && <div className="text-xs text-purple-300 mb-2">{sub}</div>}
                  <p className="text-sm text-slate-300 leading-relaxed text-justify">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Síntese / Conselhos / Direções */}
          {Array.isArray((report as any)?.five_texts) && (
            <div>
              <SectionTitle>Síntese e Direções</SectionTitle>
              <div className="space-y-4">
                {[
                  { label: 'Síntese', text: (report as any).five_texts[2] },
                  { label: 'Conselhos Práticos', text: (report as any).five_texts[3] },
                  { label: 'Direções Futuras', text: (report as any).five_texts[4] },
                ].map(({ label, text }) => text && (
                  <div key={label} className="border-l-2 border-purple-500/40 pl-4">
                    <div className="text-xs text-purple-300 uppercase tracking-widest mb-1">{label}</div>
                    <p className="text-sm text-slate-300 leading-relaxed text-justify">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Elementos e Modalidades */}
          {(chartData as any)?.elements_modalities && (
            <div>
              <SectionTitle>Elementos e Modalidades</SectionTitle>
              <p className="text-xs text-slate-500 leading-relaxed mb-4">
                Os elementos (Fogo, Terra, Ar e Água) mostram sua energia predominante; as modalidades (Cardinal, Fixa, Mutável) revelam como você age com ela.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4">
                  <div className="text-xs text-slate-400 uppercase tracking-widest mb-3">Elementos</div>
                  <div className="space-y-2">
                    {[['🔥 Fogo','Fogo'],['💧 Água','Água'],['🌍 Terra','Terra'],['💨 Ar','Ar']].map(([label, key]) => {
                      const val = (chartData as any).elements_modalities.elements[key] || 0;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <span className="text-xs text-slate-300 w-16">{label}</span>
                          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" style={{ width: `${Math.min(val * 15, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-300 w-4 text-right">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                  {(report as any)?.elements_text && <p className="text-xs text-slate-500 leading-relaxed mt-3 text-justify">{String((report as any).elements_text)}</p>}
                </div>
                <div className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4">
                  <div className="text-xs text-slate-400 uppercase tracking-widest mb-3">Modalidades</div>
                  <div className="space-y-2">
                    {[['Cardinal','Cardinal'],['Fixa','Fixa'],['Mutável','Mutável']].map(([label, key]) => {
                      const val = (chartData as any).elements_modalities.modalities[key] || 0;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <span className="text-xs text-slate-300 w-16">{label}</span>
                          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full" style={{ width: `${Math.min(val * 20, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-300 w-4 text-right">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                  {(report as any)?.modalities_text && <p className="text-xs text-slate-500 leading-relaxed mt-3 text-justify">{String((report as any).modalities_text)}</p>}
                </div>
                <div className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-4">
                  <div className="text-xs text-slate-400 uppercase tracking-widest mb-3">Polarização</div>
                  {(() => {
                    const e = (chartData as any).elements_modalities.elements || {};
                    const ativa = (e['Fogo'] || 0) + (e['Ar'] || 0);
                    const receptiva = (e['Terra'] || 0) + (e['Água'] || 0);
                    const total = ativa + receptiva || 1;
                    return (
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-xs text-slate-400 mb-1"><span>Ativa (Fogo+Ar)</span><span>{ativa}</span></div>
                          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full" style={{ width: `${(ativa/total)*100}%` }} /></div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs text-slate-400 mb-1"><span>Receptiva (Terra+Água)</span><span>{receptiva}</span></div>
                          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-400 to-teal-400 rounded-full" style={{ width: `${(receptiva/total)*100}%` }} /></div>
                        </div>
                        {(report as any)?.polarization_text && <p className="text-xs text-slate-500 leading-relaxed mt-2 text-justify">{String((report as any).polarization_text)}</p>}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Potenciais */}
          {potentials && (Array.isArray(potentials.harmonic) || Array.isArray(potentials.disharmonic)) && (
            <div>
              <SectionTitle>Potenciais</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/[0.04] border border-emerald-500/20 rounded-xl p-5">
                  <div className="text-xs text-emerald-400 uppercase tracking-widest mb-3">Harmônicos</div>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(potentials.harmonic) && potentials.harmonic.map((t: string, i: number) => (
                      <span key={i} className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-300 capitalize">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="bg-white/[0.04] border border-red-500/20 rounded-xl p-5">
                  <div className="text-xs text-red-400 uppercase tracking-widest mb-3">Desarmônicos</div>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(potentials.disharmonic) && potentials.disharmonic.map((t: string, i: number) => (
                      <span key={i} className="px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-xs text-red-300 capitalize">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Arquétipo Dominante */}
          {(chartData as any)?.archetype?.archetype && (
            <div>
              <SectionTitle>Arquétipo Dominante</SectionTitle>
              <div className="bg-white/[0.08] border border-white/[0.14] rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-purple-500/15 border border-purple-500/30 rounded-full text-sm font-semibold text-purple-300">
                    {(chartData as any).archetype.archetype}
                  </span>
                  {(chartData as any).archetype.planet && (
                    <span className="text-xs text-slate-500">regido por {(chartData as any).archetype.planet}</span>
                  )}
                </div>
                {(report as any)?.archetype_text && (
                  <p className="text-sm text-slate-300 leading-relaxed text-justify">{String((report as any).archetype_text)}</p>
                )}
              </div>
            </div>
          )}

          {/* Roda zodiacal */}
          {chartData?.chart_image && (
            <div>
              <SectionTitle>Roda Zodiacal</SectionTitle>
              <div className="flex justify-center">
                <img src={chartData.chart_image} alt="Mapa astral" className="max-h-[420px] rounded-2xl border border-white/10" />
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
export default FullAstralMap;
